import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Each section gets its own nav links
const sectionNavs = {
  product: [
    { path: '/product', label: 'Work' },
    { path: '/product/about', label: 'About' },
  ],
  experiential: [
    { path: '/experiential', label: 'Home' },
    { path: '/experiential/immersive', label: 'Immersive' },
    { path: '/experiential/digital', label: 'Digital' },
    { path: '/experiential/concepts', label: 'Concepts' },
    { path: '/experiential/about', label: 'About' },
  ],
  consulting: [
    { path: '/consulting', label: 'Services' },
  ],
  full: [
    { path: '/product', label: 'Product' },
    { path: '/experiential', label: 'Experiential' },
    { path: '/consulting', label: 'Consulting' },
    { path: '/lab', label: 'Lab' },
    { path: '/about', label: 'About' },
  ],
};

function Navigation({ section = 'full' }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = sectionNavs[section] || sectionNavs.full;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const menuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
    },
    open: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const linkVariants = {
    closed: { opacity: 0, x: 50 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <>
      <motion.nav
        className={`navigation ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <span className="logo-text">Samantha</span>
            <span className="logo-accent">de Nicola</span>
          </Link>

          <ul className="nav-menu desktop-menu">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <motion.div
                      className="nav-indicator"
                      layoutId="navIndicator"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                </Link>
              </li>
            ))}
            {(section === 'product' || section === 'consulting') && (
              <li>
                <a href="mailto:sami@samidenicola.com" className="nav-link">
                  Contact
                </a>
              </li>
            )}
          </ul>

          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <ul className="mobile-nav-menu">
              {navLinks.map((link, i) => (
                <motion.li
                  key={link.path}
                  variants={linkVariants}
                  custom={i}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  <Link
                    to={link.path}
                    className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  >
                    <span className="mobile-link-number">0{i + 1}</span>
                    <span className="mobile-link-text">{link.label}</span>
                  </Link>
                </motion.li>
              ))}
              {(section === 'product' || section === 'consulting') && (
                <motion.li
                  variants={linkVariants}
                  custom={navLinks.length}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  <a href="mailto:sami@samidenicola.com" className="mobile-nav-link">
                    <span className="mobile-link-number">0{navLinks.length + 1}</span>
                    <span className="mobile-link-text">Contact</span>
                  </a>
                </motion.li>
              )}
            </ul>

            <motion.div
              className="mobile-menu-footer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p>Los Angeles, CA</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;
